<template>
  <main class="container mx-auto mt-16 md:min-h-[calc(100vh-225px)]">
    <div
      v-if="isSubmitted"
      class="w-[90%] mx-auto md:w-3/4 lg:w-1/2 md:mx-0 text-bw-900 py-11 flex flex-col gap-6"
    >
      <NuxtIcon
        name="check-circle"
        filled
        class="check-circle-icon w-fit block p-3 md:p-4 bg-[#A2D7C3] rounded-full bg-opacity-40"
      />
      <h1 class="text-2xl md:text-4xl !leading-normal text-bw-900 font-medium">
        Thank you for Applying {{ careersData.attributes?.title }} Position
      </h1>
      <p class="text-bw-800 text-sm md:text-base">
        We'll notify to you soon, via email.
      </p>
      <NuxtLink
        to="/careers"
        title="Careers"
        class="px-3 py-[10px] sm:py-3 sm:px-6 text-sm sm:text-base border border-[#FF6700] rounded-md text-[#FF6700] block w-max hover:bg-[#FF6700] hover:text-white mt-3"
      >
        Back to Careers
      </NuxtLink>
    </div>

    <template v-else>
      <CommonBackToButton
        text-to="Careers"
        url="/careers"
        class="pt-5 md:pt-12"
      />

      <section class="flex flex-col gap-5 mt-7">
        <template v-if="isLoading">
          <UtilsSkeletonLoading class="w-1/2 md:w-2/5 h-11" />
          <UtilsSkeletonLoading class="w-10 md:w-[5%] h-8" />
          <UtilsSkeletonLoading class="w-20 md:w-[10%] h-8" />
        </template>
        <template v-else>
          <h1
            class="text-bw-900 font-medium text-[22px] leading-normal sm:text-4xl md:text-5xl lg:leading-normal"
          >
            {{ careersData.attributes?.title }}
          </h1>
          <div class="type">
            <p class="text-sm text-bw-400 mb-2">Type</p>
            <p class="text-base text-bw-800 capitalize">
              {{ careersData.attributes?.type }}
            </p>
          </div>
        </template>
      </section>

      <section
        id="content"
        class="relative flex flex-col md:flex-row w-[92%] md:w-full gap-x-4 md:mt-14"
      >
        <aside
          class="self-start md:w-[10%] mt-8 md:mt-3 flex flex-row gap-4 md:flex-col md:sticky md:float-left md:top-[100px] md:left-0"
        >
          <div
            v-if="isLoading"
            class="flex flex-row md:flex-col md:items-center gap-4 mt-6"
          >
            <UtilsSkeletonLoading class="w-14 h-10" />
            <UtilsSkeletonLoading class="w-10 h-10" />
            <UtilsSkeletonLoading class="w-10 h-10" />
            <UtilsSkeletonLoading class="w-10 h-10" />
          </div>
          <template v-else>
            <span class="text-base self-center md:self-auto text-bw-900"
              >Share</span
            >
            <button @click="copyLink">
              <NuxtIcon
                name="copy-link"
                filled
                class="share-icon cursor-pointer"
              />
            </button>
            <CommonSocialShare :social-media="socMedList" />
          </template>
        </aside>

        <div class="w-full md:w-2/3 mt-3 text-bw-900">
          <template v-if="isLoading">
            <UtilsSkeletonLoading class="w-14 h-8" />
            <UtilsSkeletonLoading class="w-full h-8 mt-3" />

            <UtilsSkeletonLoading class="w-32 h-8 mt-8" />
            <UtilsSkeletonLoading class="w-4/5 h-8 mt-3" />
            <UtilsSkeletonLoading class="w-2/5 h-8 mt-3" />
            <UtilsSkeletonLoading class="w-3/5 h-8 mt-3" />

            <UtilsSkeletonLoading class="w-32 h-8 mt-8" />
            <UtilsSkeletonLoading class="w-4/5 h-8 mt-3" />
            <UtilsSkeletonLoading class="w-full h-8 mt-3" />
            <UtilsSkeletonLoading class="w-3/4 h-8 mt-3" />
          </template>

          <div
            v-else
            class="w-full detail-content"
            v-html="careersData.attributes?.content"
          ></div>

          <div v-if="!isLoading" class="apply-form my-6 md:my-10 text-bw-900">
            <NuxtLink
              v-if="careers.data.attributes.is_ats"
              :to="careers.data.attributes.ats_url"
              target="__blank"
              class="py-3 px-[10px] sm:px-4 sm:py-3 text-[16px] leading-normal sm:text-[18px] border border-[#FF6700] rounded-md text-[#FF6700] w-max hover:bg-[#FF6700] hover:text-white disabled:bg-[#F2F2F2] disabled:text-[#B9BABD] disabled:border-0 flex font-medium"
            >
              Apply for this Job
            </NuxtLink>
            <template v-else>
              <h2
                class="text-[22px] leading-normal sm:text-[40px] sm:leading-[50px] font-medium"
              >
                Apply for this Job
              </h2>
              <form class="mt-8 w-full md:w-[70%]" @submit.prevent="applyJob">
                <div class="mb-6">
                  <label for="name" class="text-bw-800 text-sm font-medium"
                    >Name<span class="text-[#C62827]">*</span></label
                  >
                  <input
                    id="name"
                    v-model.trim="formData.name"
                    autocomplete="off"
                    required
                    type="text"
                    placeholder="Type your name"
                    class="placeholder:font-normal form-input placeholder:text-bw-400 w-full border-b border-bw-400 px-4 py-3 focus-visible:outline-none text-bw-900"
                    :class="{
                      'error-input': v$.name.$error,
                      '--disabled-input': careers.data.attributes.is_ats
                    }"
                    :disabled="careers.data.attributes.is_ats"
                    @blur="v$.name.$touch()"
                  />
                  <div
                    v-for="error in v$.name.$errors"
                    :key="error.$uid"
                    class="mt-2"
                  >
                    <div class="text-red-600 text-sm flex items-center gap-2">
                      <NuxtIcon name="cross" /> {{ error.$message }}
                    </div>
                  </div>
                </div>

                <div class="mb-6">
                  <label for="email" class="text-bw-800 text-sm font-medium"
                    >Email<span class="text-[#C62827]">*</span></label
                  >
                  <input
                    id="email"
                    v-model.trim="formData.email"
                    autocomplete="off"
                    required
                    type="email"
                    placeholder="Type your e-mail"
                    class="placeholder:font-normal form-input placeholder:text-bw-400 w-full border-b border-bw-400 px-4 py-3 focus-visible:outline-none text-bw-900"
                    :class="{
                      'error-input': v$.email.$error,
                      '--disabled-input': careers.data.attributes.is_ats
                    }"
                    :disabled="careers.data.attributes.is_ats"
                    @blur="v$.email.$touch()"
                  />
                  <div
                    v-for="error in v$.email.$errors"
                    :key="error.$uid"
                    class="mt-2"
                  >
                    <div class="text-red-600 text-sm flex items-center gap-2">
                      <NuxtIcon name="cross" /> {{ error.$message }}
                    </div>
                  </div>
                </div>

                <div class="mb-6">
                  <label for="CV" class="text-bw-800 text-sm font-medium"
                    >CV<span class="text-[#C62827]">*</span></label
                  >
                  <input
                    id="CV"
                    ref="cv"
                    required
                    type="file"
                    placeholder="Your CV"
                    class="placeholder:font-normal form-input placeholder:text-bw-400 w-full border-b border-bw-400 px-4 py-3 focus-visible:outline-none text-bw-900 hidden"
                    accept="application/pdf"
                    autocomplete="off"
                    :disabled="careers.data.attributes.is_ats"
                    @change="onInputCV"
                  />
                  <label
                    for="CV"
                    class="form-input w-full border-b border-bw-400 px-4 py-2 focus-visible:outline-none flex justify-between"
                    :class="{
                      '--disabled-input': careers.data.attributes.is_ats
                    }"
                  >
                    <span class="text-bw-400 text-base self-center">{{
                      formData.cv ? formData.cv.name : 'Your CV'
                    }}</span>
                    <span
                      class="text-xs p-2 border font-medium rounded-md"
                      :class="
                        careers.data.attributes.is_ats
                          ? 'cursor-not-allowed bg-gray-100'
                          : 'text-primary-primary hover:bg-primary-primary hover:text-white cursor-pointer border-primary-primary'
                      "
                      >Upload File</span
                    >
                  </label>
                </div>

                <div class="mb-6">
                  <label
                    for="portofolio"
                    class="text-bw-800 text-sm font-medium"
                    >Portfolio<span class="text-[#C62827]">*</span></label
                  >
                  <input
                    id="portofolio"
                    v-model.trim="formData.portofolio"
                    required
                    type="text"
                    placeholder="Your portfolio or LinkedIn URL"
                    class="placeholder:font-normal form-input placeholder:text-bw-400 w-full border-b border-bw-400 px-4 py-3 focus-visible:outline-none text-bw-900"
                    :class="{
                      'error-input': v$.portofolio.$error,
                      '--disabled-input': careers.data.attributes.is_ats
                    }"
                    autocomplete="off"
                    :disabled="careers.data.attributes.is_ats"
                    @blur="v$.portofolio.$touch()"
                  />
                  <div
                    v-for="error in v$.portofolio.$errors"
                    :key="error.$uid"
                    class="mt-2"
                  >
                    <div class="text-red-600 text-sm flex items-center gap-2">
                      <NuxtIcon name="cross" /> {{ error.$message }}
                    </div>
                  </div>
                </div>
                <button
                  type="submit"
                  class="py-3 px-[10px] sm:px-4 sm:py-3 text-sm sm:text-base border border-[#FF6700] rounded-md text-[#FF6700] w-max hover:bg-[#FF6700] hover:text-white disabled:bg-[#F2F2F2] disabled:text-[#B9BABD] disabled:border-0 flex"
                  :disabled="
                    v$.$invalid || formData.cv === null || isSubmitting
                  "
                >
                  <span v-if="isSubmitting" class="spinner w-6 h-6"></span>
                  Submit
                </button>
                <template v-if="isError">
                  <div
                    v-for="(err, index) in errorResult.error.details.errors"
                    :key="index"
                    class="text-red-600 text-sm mt-4"
                  >
                    {{ err.message }}
                  </div>
                </template>
              </form>
            </template>
          </div>
        </div>
      </section>
    </template>
  </main>
</template>

<script setup>
import { useVuelidate } from '@vuelidate/core'
import { required, email, minLength, maxLength } from '@vuelidate/validators'
import { VueReCaptcha, useReCaptcha } from 'vue-recaptcha-v3'
import debug from 'debug'
import { useGlobalStore } from '~/store/global'

const runtimeConfig = useRuntimeConfig()
const global = useGlobalStore()
const mainSeo = global.mainSeo
const mainBanner = global.mainBanner

const isLoading = ref(true)
const route = useRoute()
const careersData = ref([])

const isSubmitting = ref(false)
const isSubmitted = ref(false)
const isError = ref(false)
const errorResult = ref({})

const formData = ref({
  name: '',
  email: '',
  cv: null,
  portofolio: ''
})
const rules = {
  name: { required, minLength: minLength(4), maxLength: maxLength(256) },
  email: {
    required,
    email,
    minLength: minLength(7),
    maxLength: maxLength(256)
  },
  portofolio: { required, minLength: minLength(4), maxLength: maxLength(256) }
}

const v$ = useVuelidate(rules, formData)

const { vueApp } = useNuxtApp()
const recaptchaInstance = useReCaptcha()

vueApp.use(VueReCaptcha, {
  siteKey: runtimeConfig.public.RECAPTCHA_SITE_KEY,
  loaderOptions: {
    renderParameters: {
      hl: 'en'
    }
  }
})

const getCareersList = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/careers/${route.params.slug}`,
    {
      query: {
        'fields[0]': 'id',
        'fields[1]': 'title',
        'fields[2]': 'slug',
        'fields[3]': 'is_ats',
        'fields[4]': 'type',
        'fields[5]': 'content'
      }
    }
  )

  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Careers does not exist`
    })
  }

  return data.value
}

const careers = await getCareersList()
careersData.value = careers.data

const currentUrl = () => {
  let locHref = ''
  if (process.client) {
    locHref = window.location.href
  }
  return locHref
}

const socMedList = ref([
  {
    name: 'whatsapp',
    title: careersData.value.attributes?.title,
    url: currentUrl(),
    description: careersData.value.attributes?.content
  },
  {
    name: 'linkedin',
    title: careersData.value.attributes?.title,
    url: currentUrl(),
    description: careersData.value.attributes?.content
  }
])

const onInputCV = e => {
  const files = e.target.files

  if (files.length < 1) {
    formData.value.cv = []
    return
  }

  formData.value.cv = files[0]
}

const applyJob = async values => {
  isSubmitting.value = true
  const bodyData = new FormData()

  const payload = {
    name: formData.value.name,
    email: formData.value.email,
    portfolio: formData.value.portofolio,
    careers: [careersData.value.id]
  }
  bodyData.append('data', JSON.stringify(payload))
  bodyData.append('files.pdf', formData.value.cv)

  const { pending, error } = await useSuitmediaApiData(`/api/applicants`, {
    method: 'post',
    cache: false,
    body: bodyData
  })

  await recaptchaInstance?.recaptchaLoaded()
  const token = await recaptchaInstance?.executeRecaptcha('submit')
  values.googleReCaptchaToken = token
  const log = debug('log-careers-slug')
  log({ token })
  log('Google recaptChaToken: ', values.googleReCaptchaToken)

  if (error.value) {
    isSubmitting.value = pending.value
    isError.value = true
    errorResult.value = error.value.data
    throw createError({
      ...error.value,
      statusCode: error.value.statusCode,
      statusMessage: error.value.statusMessage
    })
  } else {
    isSubmitting.value = pending.value
    isSubmitted.value = true
    clearForm()
  }
}

const clearForm = () => {
  formData.value.name = ''
  formData.value.email = ''
  formData.value.portofolio = ''
  formData.value.cv = null
  v$.value.$reset()
}

const copyLink = () => {
  const el = document.createElement('textarea')
  el.value = currentUrl()
  el.setAttribute('readonly', '')
  el.style.position = 'absolute'
  el.style.left = '-9999px'
  document.body.appendChild(el)
  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)
  const tooltip = document.querySelectorAll('.tooltiptext')
  Array.prototype.forEach.call(tooltip, function (element) {
    element.innerHTML = 'Copied'
  })
  setTimeout(function () {
    Array.prototype.forEach.call(tooltip, function (element) {
      element.innerHTML = 'Copy link'
    })
  }, 2000)
}

const metaDescription =
  `Join Suitmedia as ${careersData.value.attributes?.title} Enjoy Work From Anywhere (WFA), contribute to innovative projects & grow your career. Apply now!` ||
  'Digital Agency Indonesia'

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name:
    `${careersData.value.attributes?.title} - Suitmedia | Remote Work` ||
    'Suitmedia | Remote Work',
  url: route.path || 'suitmedia.com',
  description: metaDescription,
  image:
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})

useSeoMeta({
  title:
    `${careersData.value.attributes?.title} - Suitmedia | Remote Work` ||
    'Suitmedia | Remote Work',
  ogTitle:
    `${careersData.value.attributes?.title} - Suitmedia | Remote Work` ||
    'Suitmedia | Remote Work',
  description: metaDescription,
  ogDescription: metaDescription,
  ogImage:
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: 'website',
  twitterTitle:
    `${careersData.value.attributes?.title} - Suitmedia | Remote Work` ||
    'Suitmedia | Remote Work',
  twitterDescription: metaDescription,
  twitterImage:
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    mainBanner.attributes?.banner?.image?.data?.attributes?.url ||
    '/icon.png'
})
</script>

<style lang="scss" scoped>
:deep(.detail-content) {
  strong {
    font-weight: 500 !important;
  }
  p {
    margin-bottom: 1rem;
  }
  .p1 {
    font-size: 24px;
    line-height: normal;
    font-weight: 500 !important;
    margin-bottom: 1rem;
    margin-top: 12px;
  }

  ol,
  ul {
    display: flex;
    flex-direction: column;
    // list-style-type: decimal;

    counter-reset: list-number;
    margin-bottom: 1rem;

    ::marker {
      color: red;
      padding: 1rem;
    }
    ::before {
      counter-increment: list-number;
      content: counter(list-number);
      padding: 5px 12px;
      width: 32px;
      height: 32px;
      background: #fef0e5;
      border-radius: 2px;
      margin-right: 12px;
      color: #ff6700;
      font-size: 14px;
    }

    li {
      margin-bottom: 12px;
      // text-indent: -43px;
      // padding-left: 44px;
      // margin-left: 22px;
      font-size: 16px;
      line-height: 24px;

      display: flex;
      align-items: center;

      ::before {
        counter-increment: none;
        content: '';
        padding: 0;
        background: none;
        margin-right: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

:deep(.share-icon) {
  svg {
    width: 40px;
    height: 40px;
    transition: all 0.2s ease-in-out;

    &:hover {
      transform: scale(1.25);
    }
  }
}
:deep(.check-circle-icon) {
  svg {
    width: 20px;
    height: 20px;
  }

  @media screen and (min-width: 768px) {
    svg {
      width: 30px;
      height: 30px;
    }
  }
}

.spinner {
  display: block;
  // width: 42px;
  // height: 42px;
  margin-right: 10px;
  border-radius: 50%;
  border: 5px solid rgba(#000, 0.1);
  border-top-color: #ff6700;
  animation: spinning 1s infinite both;
}
@keyframes spinning {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.--disabled-input {
  @apply bg-gray-100 cursor-not-allowed;
}
</style>
